/** Per-template send settings — stored on server, never in site list API */
export type EmailContentFormat = 'html' | 'text';

export type EmailProvider = 'resend' | 'ses';

export interface EmailDeliveryConfig {
  siteId?: string;
  label?: string;
  from?: string;
  bcc?: string[];
  replyTo?: string;
  frontendUrl?: string;
  inviteRole?: string;
  inviteCampaign?: string;
  contentFormat?: EmailContentFormat;
  emailProvider?: EmailProvider;
}

export interface EmailSiteSummary {
  id: string;
  label: string;
  domain: string;
  frontendUrl: string;
  emailProvider: EmailProvider;
}

export interface EmailSitesResponse {
  success: boolean;
  defaultSiteId: string;
  sites: EmailSiteSummary[];
  /** All configured brands for content checks (safe public fields only). */
  allBrands?: EmailSiteSummary[];
}

export function parseBccInput(value: string): string[] | undefined {
  const list = value
    .split(/[,;]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
  return list.length > 0 ? list : undefined;
}

export function formatBccInput(value?: string[]): string {
  return value?.join(', ') ?? '';
}

export function buildDeliveryConfigFromForm(input: {
  siteId: string;
  from?: string;
  bccText?: string;
  replyTo?: string;
  label?: string;
  frontendUrl?: string;
  inviteRole?: string;
  inviteCampaign?: string;
  contentFormat?: EmailContentFormat;
  emailProvider?: EmailProvider | '';
}): EmailDeliveryConfig | undefined {
  const config: EmailDeliveryConfig = {};
  if (input.siteId) config.siteId = input.siteId;
  if (input.from?.trim()) config.from = input.from.trim();
  if (input.replyTo?.trim()) config.replyTo = input.replyTo.trim();
  if (input.label?.trim()) config.label = input.label.trim();
  if (input.frontendUrl?.trim()) config.frontendUrl = input.frontendUrl.trim();
  if (input.inviteRole?.trim()) config.inviteRole = input.inviteRole.trim();
  if (input.inviteCampaign?.trim()) config.inviteCampaign = input.inviteCampaign.trim();
  if (input.contentFormat === 'text' || input.contentFormat === 'html') {
    config.contentFormat = input.contentFormat;
  }
  if (input.emailProvider === 'resend' || input.emailProvider === 'ses') {
    config.emailProvider = input.emailProvider;
  }
  const bcc = parseBccInput(input.bccText ?? '');
  if (bcc) config.bcc = bcc;

  return Object.keys(config).length > 0 ? config : undefined;
}
